// src/components/Navigation.js

import React, { useState, useContext } from 'react';
import { AppBar, Toolbar, IconButton, Typography, Menu, MenuItem, Box, Avatar, Stack, useMediaQuery } from '@mui/material';
import { Menu as MenuIcon, AccountCircle } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../contexts/AuthContext';
import { useTheme, alpha } from '@mui/material/styles';
import { BRAND } from '../theme';

const Navigation = ({ handleDrawerToggle, showDesktopDrawerToggle = false, desktopSidebarWidth = 0 }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const navigate = useNavigate();
    const { logout, user } = useContext(AuthContext); // Access user from AuthContext
    const theme = useTheme();
    const isCompactTopBar = useMediaQuery(theme.breakpoints.down('lg'));
    const userInitial = String(user?.FirstName || user?.Username || 'I').trim().charAt(0).toUpperCase();

    const handleLogout = async () => {
        try {
            // Log the sign-out event first, before removing the token
        await axios.post('/api/systemlogs', {
            event: 'signout',
            userId: user?.Id || 0,
            username: user?.Username || 'Unknown User',
            entityId: user?.Id || 0,
            entityType: 'user.logout',
            description: `${user?.Username || 'User'} signed out.`,
        });
            await axios.post('/api/auth/logout');
            sessionStorage.removeItem('token');
            logout();
            navigate('/login');
        } catch (error) {
            console.error('Logout error:', error);
        }
    };

    const handleProfileClick = () => {
        navigate('/profile');
    };

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    return (
        <AppBar
            position="fixed"
            elevation={0}
            sx={{
                zIndex: 1200,
                ml: { xs: 0, sm: showDesktopDrawerToggle ? 0 : `${desktopSidebarWidth}px` },
                width: {
                    xs: '100%',
                    sm: showDesktopDrawerToggle ? '100%' : `calc(100% - ${desktopSidebarWidth}px)`,
                },
                background: BRAND.dark,
                borderBottom: `1px solid ${alpha('#ffffff', 0.12)}`,
                boxShadow: '0 10px 24px rgba(15, 23, 42, 0.16)',
                backdropFilter: 'blur(10px)',
            }}
        >
            <Toolbar sx={{ minHeight: { xs: 52, sm: 54 }, px: { xs: 1, sm: 1.5 } }}>
                {/* Menu button for mobile screens */}
                <IconButton
                    color="inherit"
                    aria-label="open drawer"
                    onClick={handleDrawerToggle}
                    sx={{
                        mr: 1,
                        display: { xs: 'inline-flex', sm: showDesktopDrawerToggle ? 'inline-flex' : 'none' },
                        width: 34,
                        height: 34,
                        borderRadius: 1.5,
                        border: `1px solid ${alpha('#ffffff', 0.16)}`,
                        backgroundColor: alpha('#ffffff', 0.08),
                        '&:hover': {
                            backgroundColor: alpha('#ffffff', 0.14),
                        },
                    }}
                >
                    <MenuIcon />
                </IconButton>

                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Stack spacing={0.1}>
                        <Typography
                            variant="caption"
                            sx={{
                                color: alpha('#ffffff', 0.72),
                                letterSpacing: 0.8,
                                lineHeight: 1,
                                textTransform: 'uppercase',
                                display: { xs: 'none', sm: 'block' },
                            }}
                        >
                            IV Creative
                        </Typography>
                        <Typography
                            variant="h6"
                            sx={{
                                color: '#fff',
                                fontWeight: 700,
                                lineHeight: 1.1,
                                fontSize: { xs: '0.92rem', sm: '1rem' },
                            }}
                        >
                            IV Linx
                        </Typography>
                    </Stack>
                </Box>

                {/* Display the username next to the profile icon */}
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {user && (
                        <Box
                            sx={{
                                display: { xs: 'none', md: isCompactTopBar ? 'none' : 'flex' },
                                alignItems: 'center',
                                mr: 1,
                                px: 0.85,
                                py: 0.35,
                                borderRadius: 99,
                                border: `1px solid ${alpha('#ffffff', 0.12)}`,
                                backgroundColor: alpha('#ffffff', 0.08),
                            }}
                        >
                            <Avatar
                                sx={{
                                    width: 24,
                                    height: 24,
                                    mr: 0.7,
                                    bgcolor: alpha('#ffffff', 0.18),
                                    color: '#fff',
                                    fontSize: '0.8rem',
                                    fontWeight: 700,
                                }}
                            >
                                {userInitial}
                            </Avatar>
                            <Box>
                                <Typography variant="caption" sx={{ color: alpha('#ffffff', 0.7), display: 'block', lineHeight: 1 }}>
                                    Signed in
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600, lineHeight: 1.2 }}>
                                    {user.FirstName} {user.LastName}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                    <IconButton
                        edge="end"
                        color="inherit"
                        aria-label="account"
                        onClick={handleMenuOpen}
                        sx={{
                            width: 34,
                            height: 34,
                            borderRadius: 1.5,
                            border: `1px solid ${alpha('#ffffff', 0.16)}`,
                            backgroundColor: alpha('#ffffff', 0.08),
                            '&:hover': {
                                backgroundColor: alpha('#ffffff', 0.14),
                            },
                        }}
                    >
                        <AccountCircle />
                    </IconButton>
                </Box>

                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    PaperProps={{
                        elevation: 0,
                        sx: {
                            mt: 1.2,
                            minWidth: 180,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            boxShadow: '0 18px 36px rgba(15, 23, 42, 0.14)',
                        },
                    }}
                >
                    <MenuItem onClick={handleProfileClick}>My Profile</MenuItem>
                    <MenuItem onClick={handleLogout}>Logout</MenuItem>
                </Menu>
            </Toolbar>
        </AppBar>
    );
};

export default Navigation;
